import React from "react";
import { useTranslation } from "react-i18next";
import { useComparisonLabels } from "../../../compare/ComparisonLabelsContext";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import { formatItalianNumber } from "utils/formatUtils";

type EsgDistributionData = {
  [key: string]: { alta: number; media: number; bassa: number };
};

type EsgDistributionProps = {
  data: EsgDistributionData;
  data2: EsgDistributionData;
};

const EsgDistributionChart: React.FC<EsgDistributionProps> = ({
  data,
  data2,
}) => {
  const { t } = useTranslation();
  const labels = useComparisonLabels();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const mapStrings = {
    lettera_a: "Lettera A",
    lettera_b: "Lettera B",
    lettera_c: "Lettera C",
    pai_e: "PAI E",
    pai_g: "PAI G",
    pai_s: "PAI S",
  };

  // Format data properly for the chart
  const formattedData = Object.keys(data).map((key) => {
    const p1Values = data[key];
    const p2Values = data2[key];

    return {
      name: mapStrings[key],
      // Portfolio 1 values
      p1_alta: p1Values.alta * 100,
      p1_media: (p1Values.media - p1Values.alta) * 100,
      p1_bassa: (p1Values.bassa - p1Values.media) * 100,
      p1_total_alta: p1Values.alta * 100,
      p1_total_media: p1Values.media * 100,
      p1_total_bassa: p1Values.bassa * 100,
      // Portfolio 2 values
      p2_alta: p2Values.alta * 100,
      p2_media: (p2Values.media - p2Values.alta) * 100,
      p2_bassa: (p2Values.bassa - p2Values.media) * 100,
      p2_total_alta: p2Values.alta * 100,
      p2_total_media: p2Values.media * 100,
      p2_total_bassa: p2Values.bassa * 100,
    };
  });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            fontSize: 12,
          }}
        >
          <p>{dataPoint.name}</p>
          <p style={{ marginBottom: "10px", fontWeight: "bold" }}>
            {labels.portfolio1}
          </p>
          <p style={{ color: colorsConfig.sostenibilitaPortfolio1Alta }}>
            {t("global.high")}: {formatItalianNumber(dataPoint.p1_total_alta, 1)}%
          </p>
          <p style={{ color: colorsConfig.sostenibilitaPortfolio1Media }}>
            {t("global.medium")}: {formatItalianNumber(dataPoint.p1_total_media, 1)}%
          </p>
          <p style={{ color: colorsConfig.sostenibilitaPortfolio1Bassa }}>
            {t("global.low")}: {formatItalianNumber(dataPoint.p1_total_bassa, 1)}%
          </p>
          <p
            style={{
              marginTop: "10px",
              marginBottom: "10px",
              fontWeight: "bold",
            }}
          >
            {labels.portfolio2}
          </p>
          <p style={{ color: colorsConfig.sostenibilitaPortfolio2Alta }}>
            {t("global.high")}: {formatItalianNumber(dataPoint.p2_total_alta, 1)}%
          </p>
          <p style={{ color: colorsConfig.sostenibilitaPortfolio2Media }}>
            {t("global.medium")}: {formatItalianNumber(dataPoint.p2_total_media, 1)}%
          </p>
          <p style={{ color: colorsConfig.sostenibilitaPortfolio2Bassa }}>
            {t("global.low")}: {formatItalianNumber(dataPoint.p2_total_bassa, 1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomBar = (props: any) => {
    const { x, y, width, height, fill, dataKey } = props;

    const isLastInStack = dataKey.includes("bassa");
    let radius = isLastInStack ? 4 : 0;

    if (width === 0) {
      radius = 0;
    }

    return (
      <path
        d={`
          M ${x},${y}
          h ${width - radius}
          ${isLastInStack ? `q ${radius},0 ${radius},${radius}` : `h 0`}
          v ${height - (isLastInStack ? 2 * radius : 0)}
          ${isLastInStack ? `q 0,${radius} -${radius},${radius}` : `v 0`}
          h ${-(width - (isLastInStack ? radius : 0))}
          Z
        `}
        fill={fill}
      />
    );
  };

  const maxValue = (() => {
    let max = 0;
    formattedData.forEach((item) => {
      const p1Total = item.p1_alta + item.p1_media + item.p1_bassa;
      const p2Total = item.p2_alta + item.p2_media + item.p2_bassa;
      max = Math.max(max, p1Total, p2Total);
    });
    return Math.ceil(max / 10) * 10;
  })();

  const xAxisTicks = Array.from(
    { length: maxValue / 10 + 1 },
    (_, i) => i * 10,
  );

  return (
    <div className="w-full">
      <div style={{ height: 500, position: "relative" }}>
        <ResponsiveContainer width="100%" height={450} debounce={500}>
          <BarChart
            data={formattedData}
            layout="vertical"
            margin={{ top: 5, right: 20, left: 120, bottom: 5 }}
            barSize={26}
            barGap={4}
            barCategoryGap={12}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={false}
              vertical={false}
            />
            <XAxis
              type="number"
              domain={[0, maxValue]}
              ticks={xAxisTicks}
              tickFormatter={(value) => `${value}%`}
              tick={{ fontSize: 12, fill: colorsConfig.FinecoBlack }}
              tickLine={false}
              axisLine={{ stroke: colorsConfig.Gray3 }}
            />
            <YAxis
              dataKey="name"
              type="category"
              tick={{
                width: 80,
                fontSize: 14,
                textAnchor: "end",
                fill: colorsConfig.FinecoBlack,
              }}
              tickLine={false}
              width={80}
              axisLine={{ stroke: colorsConfig.Gray3 }}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Portfolio 1 bars */}
            <Bar
              dataKey="p1_alta"
              fill={colorsConfig.sostenibilitaPortfolio1Alta}
              stackId="stack1"
              shape={<CustomBar />}
              name="p1_high"
              isAnimationActive={false}
            />
            <Bar
              dataKey="p1_media"
              fill={colorsConfig.sostenibilitaPortfolio1Media}
              stackId="stack1"
              shape={<CustomBar />}
              name="p1_medium"
              isAnimationActive={false}
            />
            <Bar
              dataKey="p1_bassa"
              fill={colorsConfig.sostenibilitaPortfolio1Bassa}
              stackId="stack1"
              shape={<CustomBar />}
              name="p1_low"
              isAnimationActive={false}
            />

            {/* Portfolio 2 bars */}
            <Bar
              dataKey="p2_alta"
              fill={colorsConfig.sostenibilitaPortfolio2Alta}
              stackId="stack2"
              shape={<CustomBar />}
              name="p2_high"
              isAnimationActive={false}
            />
            <Bar
              dataKey="p2_media"
              fill={colorsConfig.sostenibilitaPortfolio2Media}
              stackId="stack2"
              shape={<CustomBar />}
              name="p2_medium"
              isAnimationActive={false}
            />
            <Bar
              dataKey="p2_bassa"
              fill={colorsConfig.sostenibilitaPortfolio2Bassa}
              stackId="stack2"
              shape={<CustomBar />}
              name="p2_low"
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <PortfolioHeaders />
    </div>
  );
};

export default EsgDistributionChart;

const PortfolioHeaders = () => {
  // Define colors
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const labels = useComparisonLabels();
  return (
    <div style={{ position: "relative", marginTop: -45 }}>
      <div
        style={{
          marginTop: 8,
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 8,
          fontSize: 12,
          width: "100%", // Using viewport width to ensure full width
        }}
      >
        {/* Left side - Portafoglio 1 */}
        <div style={{ display: "flex" }}>
          <span style={{ fontWeight: 500 }}>{labels.portfolio1}</span>
          <div style={{ marginLeft: 16, display: "flex" }}>
            <div
              style={{
                marginRight: 16,
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  marginRight: 4,
                  height: 12,
                  width: 12,
                  borderRadius: "50%",
                  backgroundColor: colorsConfig.sostenibilitaPortfolio1Alta,
                }}
              ></div>
              <span>Alta</span>
            </div>
            <div
              style={{
                marginRight: 16,
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  marginRight: 4,
                  height: 12,
                  width: 12,
                  borderRadius: "50%",
                  backgroundColor: colorsConfig.sostenibilitaPortfolio1Media,
                }}
              ></div>
              <span>Media</span>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  marginRight: 4,
                  height: 12,
                  width: 12,
                  borderRadius: "50%",
                  backgroundColor: colorsConfig.sostenibilitaPortfolio1Bassa,
                }}
              ></div>
              <span>Bassa</span>
            </div>
          </div>
        </div>

        {/* Right side - Portafoglio 2 (positioned to fit within container) */}
        <div
          style={{
            display: "flex",
            marginLeft: "auto", // Push to the right side
            paddingRight: 24,
          }}
        >
          <span style={{ fontWeight: 500 }}>{labels.portfolio2}</span>
          <div style={{ marginLeft: 16, display: "flex" }}>
            <div
              style={{
                marginRight: 16,
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  marginRight: 4,
                  height: 12,
                  width: 12,
                  borderRadius: "50%",
                  backgroundColor: colorsConfig.sostenibilitaPortfolio2Alta,
                }}
              ></div>
              <span>Alta</span>
            </div>
            <div
              style={{
                marginRight: 16,
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  marginRight: 4,
                  height: 12,
                  width: 12,
                  borderRadius: "50%",
                  backgroundColor: colorsConfig.sostenibilitaPortfolio2Media,
                }}
              ></div>
              <span>Media</span>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  marginRight: 4,
                  height: 12,
                  width: 12,
                  borderRadius: "50%",
                  backgroundColor: colorsConfig.sostenibilitaPortfolio2Bassa,
                }}
              ></div>
              <span>Bassa</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
